import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  IconButton,
  LinearProgress,
  Skeleton,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';

const renderCellContent = (column, row) => {
  if (typeof column.render === 'function') {
    return column.render(row.rowData, row);
  }
  const value = row.rowData ? row.rowData[column.name] : undefined;
  return typeof value === 'function' ? value() : value;
};

const compareValues = (a, b) => {
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

/**
 * Modern, dependency-light data table built on MUI v5. Replaces the slow
 * `DataGridWithHeaders` for the Super Admin tools. Supports client/server
 * pagination, optional client-side sorting and per-cell render functions.
 */
const DataTable = ({
  columns,
  rows,
  loading,
  getRowId,
  title,
  subtitle,
  onRefresh,
  toolbarStart,
  toolbarEnd,
  emptyState,
  paginationMode,
  page,
  pageSize,
  pageSizeOptions,
  totalCount,
  onPageChange,
  onPageSizeChange,
  dense,
  'data-testid': dataTestId,
}) => {
  const visibleColumns = useMemo(
    () => columns.filter((column) => column.isVisible !== false),
    [columns]
  );

  const isServer = paginationMode === 'server';

  const [clientPage, setClientPage] = useState(0);
  const [clientSize, setClientSize] = useState(pageSize);

  useEffect(() => {
    if (!isServer && page != null) {
      setClientPage(Math.max(0, page - 1));
    }
  }, [page, isServer]);

  useEffect(() => {
    if (!isServer && pageSize != null) {
      setClientSize(pageSize);
    }
  }, [pageSize, isServer]);
  const [sort, setSort] = useState({ name: null, direction: 'asc' });

  const sortedRows = useMemo(() => {
    if (isServer || !sort.name) return rows;
    const column = visibleColumns.find((col) => col.name === sort.name);
    if (!column) return rows;
    const accessor = (row) => {
      if (typeof column.sortValue === 'function') return column.sortValue(row.rowData, row);
      const value = row.rowData ? row.rowData[column.name] : undefined;
      return typeof value === 'function' ? undefined : value;
    };
    const next = [...rows].sort((a, b) => compareValues(accessor(a), accessor(b)));
    return sort.direction === 'desc' ? next.reverse() : next;
  }, [rows, sort, isServer, visibleColumns]);

  const effectivePage = isServer ? page - 1 : clientPage;
  const effectiveSize = isServer ? pageSize : clientSize;

  const pageRows = useMemo(() => {
    if (isServer) return sortedRows;
    const start = clientPage * clientSize;
    return sortedRows.slice(start, start + clientSize);
  }, [sortedRows, isServer, clientPage, clientSize]);

  const count = isServer ? totalCount : sortedRows.length;

  const handleChangePage = (_event, nextZeroBased) => {
    if (isServer) {
      onPageChange?.(nextZeroBased + 1);
    } else {
      setClientPage(nextZeroBased);
      onPageChange?.(nextZeroBased + 1);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    const nextSize = parseInt(event.target.value, 10);
    if (isServer) {
      onPageSizeChange?.(nextSize);
    } else {
      setClientSize(nextSize);
      setClientPage(0);
      onPageSizeChange?.(nextSize);
      onPageChange?.(1);
    }
  };

  const handleSort = (column) => {
    if (!column.sortable) return;
    setSort((prev) => {
      if (prev.name !== column.name) return { name: column.name, direction: 'asc' };
      return { name: column.name, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
    });
  };

  const hasToolbar = title || subtitle || onRefresh || toolbarStart || toolbarEnd;
  const showInitialLoading = loading && rows.length === 0;
  const isRefreshing = loading && rows.length > 0;
  const showEmpty = !loading && pageRows.length === 0;

  const getAriaSort = (column) => {
    if (!column.sortable || isServer || sort.name !== column.name) return undefined;
    return sort.direction === 'asc' ? 'ascending' : 'descending';
  };

  const renderHeaderCell = (column) => {
    if (column.renderHeader) return column.renderHeader();
    if (column.sortable && !isServer) {
      return (
        <TableSortLabel
          active={sort.name === column.name}
          direction={sort.name === column.name ? sort.direction : 'asc'}
          onClick={() => handleSort(column)}
          aria-label={`Sort by ${column.displayName}`}
        >
          {column.displayName}
        </TableSortLabel>
      );
    }
    return column.displayName;
  };

  return (
    <Paper
      elevation={0}
      variant="outlined"
      data-testid={dataTestId}
      sx={{ borderColor: 'divider', overflow: 'hidden', backgroundColor: 'common.white' }}
    >
      {hasToolbar && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1.5,
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ minWidth: 0, mr: 'auto' }}>
            {title && (
              <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          {toolbarStart}
          {toolbarEnd}
          {onRefresh && (
            <Tooltip title="Refresh">
              <span>
                <IconButton
                  size="small"
                  onClick={onRefresh}
                  disabled={loading}
                  aria-label="Refresh"
                  data-testid="data-table-refresh"
                >
                  <RefreshRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      )}

      {isRefreshing && (
        <LinearProgress
          aria-label="Refreshing table"
          data-testid="data-table-refresh-progress"
          sx={{ height: 2 }}
        />
      )}

      <TableContainer sx={{ maxHeight: 560, overflowX: 'auto', position: 'relative' }}>
        <Table stickyHeader size={dense ? 'small' : 'medium'} aria-label={title || 'data table'}>
          <TableHead>
            <TableRow>
              {visibleColumns.map((column) => (
                <TableCell
                  key={column.name}
                  scope="col"
                  align={column.align || 'left'}
                  aria-sort={getAriaSort(column)}
                  sortDirection={sort.name === column.name ? sort.direction : false}
                  sx={{
                    fontWeight: 700,
                    color: 'text.secondary',
                    backgroundColor: 'grey.50',
                    whiteSpace: 'nowrap',
                    width: column.width,
                    fontSize: '0.75rem',
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    py: 1.25,
                    '& .MuiTableSortLabel-root.Mui-active': { color: 'primary.main' },
                    '& .MuiTableSortLabel-root:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: 2,
                      borderRadius: 1,
                    },
                  }}
                >
                  {renderHeaderCell(column)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {showInitialLoading &&
              Array.from({ length: 5 }).map((_, rowIndex) => (
                // eslint-disable-next-line react/no-array-index-key
                <TableRow key={`skeleton-${rowIndex}`}>
                  {visibleColumns.map((column) => (
                    <TableCell key={column.name}>
                      <Skeleton variant="text" width="80%" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}

            {!showInitialLoading &&
              pageRows.map((row, index) => {
                const rowId = getRowId ? getRowId(row) : row.rowData?.id ?? index;
                return (
                  <TableRow
                    key={rowId}
                    hover
                    selected={Boolean(row.isSelected)}
                    sx={{
                      transition: 'background-color 120ms ease',
                      '&:last-child td': { borderBottom: 0 },
                      '&.MuiTableRow-hover:hover': { backgroundColor: 'grey.50' },
                      '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: 'primary.light' },
                      '&.Mui-selected td:first-of-type': {
                        boxShadow: (theme) => `inset 3px 0 0 ${theme.palette.primary.main}`,
                      },
                    }}
                  >
                    {visibleColumns.map((column) => (
                      <TableCell
                        key={column.name}
                        align={column.align || 'left'}
                        sx={{ verticalAlign: 'middle', color: 'text.primary', py: 1.5 }}
                      >
                        {renderCellContent(column, row)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}

            {showEmpty && (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} sx={{ borderBottom: 0, py: 6 }}>
                  {emptyState || (
                    <Typography align="center" color="text.secondary">
                      No records to display.
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={count}
        page={count > 0 ? effectivePage : 0}
        onPageChange={handleChangePage}
        rowsPerPage={effectiveSize}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={pageSizeOptions}
        showFirstButton
        showLastButton
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          '& .MuiTablePagination-toolbar': {
            alignItems: 'center',
            minHeight: 52,
            px: 2,
          },
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            margin: 0,
            lineHeight: 1.5,
          },
          '& .MuiTablePagination-select': {
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiInputBase-root': {
            marginTop: 0,
            marginBottom: 0,
          },
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            py: 0.5,
          },
        }}
      />
    </Paper>
  );
};

DataTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  getRowId: PropTypes.func,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  onRefresh: PropTypes.func,
  toolbarStart: PropTypes.node,
  toolbarEnd: PropTypes.node,
  emptyState: PropTypes.node,
  paginationMode: PropTypes.oneOf(['client', 'server']),
  page: PropTypes.number,
  pageSize: PropTypes.number,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
  totalCount: PropTypes.number,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  dense: PropTypes.bool,
  'data-testid': PropTypes.string,
};

DataTable.defaultProps = {
  rows: [],
  loading: false,
  getRowId: undefined,
  title: undefined,
  subtitle: undefined,
  onRefresh: undefined,
  toolbarStart: null,
  toolbarEnd: null,
  emptyState: null,
  paginationMode: 'client',
  page: 1,
  pageSize: 25,
  pageSizeOptions: [10, 25, 50, 100],
  totalCount: 0,
  onPageChange: undefined,
  onPageSizeChange: undefined,
  dense: false,
  'data-testid': undefined,
};

export default DataTable;
